import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Illustration,
  Loader,
  Typography,
} from '@saviynt/design-system';

import config from '../../../../config/obNextGenConfig';
import useLocalization from '../../../../hooks/use-localize';
import { useTypedSelector } from '../../../../hooks/use-typedselector';
import Logger from '../../../../services/loggerService';
import { type StartImportRequest } from '../../data-layer/types';
import { useStartImportMutation } from '../../data-store/DiscoveryFileUploadApi';
import {
  resetDiscoveryFileUploadState,
  setIsStartImport,
  setStartImportResponse,
} from '../../data-store/DiscoveryFileUploadSlice';

import './StartImportModalContent.css';

/**
 * Modal content component for handling file import process.
 * Shows loading state, success state, and error state with appropriate actions.
 *
 * @param {number|string|null} fileUploadIobAppId - The IOB application ID
 * @param {number|string|null} fileUploadSourceAppId - The source application ID
 * @param {() => void} handleCloseModal - Callback function to close the modal
 */
function StartImportModalContent({
  fileUploadIobAppId,
  fileUploadSourceAppId,
  handleCloseModal,
}: {
  fileUploadIobAppId: number | string | null;
  fileUploadSourceAppId: number | string | null;
  handleCloseModal: () => void;
}) {
  const [startImport] = useStartImportMutation();

  const dispatch = useDispatch();
  const history = useHistory();
  const formatMessage = useLocalization();

  const startImportResponse = useTypedSelector(
    (state) => state.discoveryFileUpload.startImportResponse
  );

  const updatedHeaders = useTypedSelector(
    (state) => state.discoveryFileUpload.updatedHeaders
  );

  const fileUploadResult = useTypedSelector(
    (state) => state.discoveryFileUpload.data
  );

  const handleStartImportClose = () => {
    handleCloseModal();
    setTimeout(() => {
      dispatch(resetDiscoveryFileUploadState());
    }, 300);
  };

  const handleStartImportSuccessClose = useCallback(() => {
    handleCloseModal();
    dispatch(resetDiscoveryFileUploadState());
    history.push(config.URL_OB_NEXT_GEN);
  }, [handleCloseModal, dispatch, history]);

  const handleStartImportTryAgain = async () => {
    dispatch(setStartImportResponse(null));

    const firstUploadResult = fileUploadResult[0];
    const { fileName } = firstUploadResult;
    const { generatedFileName } = firstUploadResult;

    dispatch(setIsStartImport(true));

    if (!fileName || !generatedFileName || fileUploadSourceAppId == null) {
      Logger.error('Missing required data to start import');

      return;
    }

    const payload: StartImportRequest = {
      appTypeKey: fileUploadSourceAppId.toString(),
      iobAppKey: fileUploadIobAppId?.toString() ?? null,
      fileName,
      schemaFileNames: generatedFileName,
      updatedHeader: updatedHeaders,
    };

    try {
      const response = await startImport(payload).unwrap();

      dispatch(setStartImportResponse(response));
    } catch (err: unknown) {
      const errorResponse =
        err instanceof Error ||
        (err && typeof err === 'object' && 'data' in err)
          ? (err as { data?: { status: number; message: string } }).data ?? {
              status: 500,
              message: 'Unknown error',
            }
          : {
              status: 500,
              message: 'Unknown error',
            };

      dispatch(setStartImportResponse(errorResponse));
    }
  };

  if (!startImportResponse) {
    return (
      <section className='StartImportModalContent'>
        <div className='StartImportModalContent-importContent-loader'>
          <Loader kind='loop' format='main' color='brand' />
          <Typography kind='body2'>
            {formatMessage(
              'INTEGRATIONS.FILEUPLOAD.IMPORTINGDATA',
              'Importing data from Excel file. Please wait...'
            )}
          </Typography>
        </div>
      </section>
    );
  }

  if (startImportResponse.status !== 200) {
    return (
      <section className='StartImportModalContent'>
        <div className='StartImportModalContent-importContent'>
          <Illustration kind='Error' />
          <Typography kind='h1'>Import Failed</Typography>
          <Typography kind='body2'>
            {formatMessage(
              'INTEGRATIONS.ERROR.CONNECTION',
              'Connection error. Please try again.'
            )}
          </Typography>
          <Button
            kind='filled'
            onClick={handleStartImportTryAgain}
            dataTestId='aob-fileupload-startimport-tryagain'>
            {formatMessage('INTEGRATIONS.TRYAGAIN', 'Try Again')}
          </Button>
          <Button
            kind='outlined'
            onClick={handleStartImportClose}
            dataTestId='aob-fileupload-startimport-close'>
            {formatMessage('INTEGRATIONS.CLOSE', 'Close')}
          </Button>
        </div>
      </section>
    );
  }

  // Success Case
  return (
    <section className='StartImportModalContent'>
      <div className='StartImportModalContent-importContent'>
        <Illustration kind='NoErrorsFound' />
        <Typography kind='h1'>Importing Data from XSL File</Typography>
        <Typography kind='body2'>
          {formatMessage(
            'INTEGRATIONS.FILEUPLOAD.SUCCESS',
            ' You can exit the page while waiting. We will notify you once the process is complete.'
          )}
        </Typography>
        <Button
          kind='filled'
          onClick={handleStartImportSuccessClose}
          dataTestId='aob-fileupload-startimport-successclose'>
          {formatMessage('INTEGRATIONS.CLOSE', 'Close')}
        </Button>
      </div>
    </section>
  );
}

export default StartImportModalContent;
